/**
 * useChecklistReplaceGuard - "Replace this checklist?" before a job form
 * swaps out a checklist that already holds work.
 *
 * Replacing a job's checklist deletes the old one with every item on it —
 * ticks, ratings, notes, critical findings — and photos lose their link to
 * the item they were taken for. The server refuses that unless the request
 * confirms (replace_progress); this is where the person confirms.
 *
 *	 const { confirmReplace, replacePrompt } = useChecklistReplaceGuard('inspection');
 *	 if (!(await confirmReplace(progressCount))) return;   // kept the old checklist
 *	 …save with replaceProgress: true…
 *	 return (<>{…the form…}{replacePrompt}</>);
 *
 * Same shape as useNotifyPrompt and useDiscardGuard: the hook owns the
 * dialog and hands back the element to render.
 */
import React, { useCallback, useRef, useState } from 'react';
import { ConfirmModal } from '../components/organisms/ConfirmModal';

export interface UseChecklistReplaceGuard {
	/** Ask before replacing; resolves true to replace, false to keep the current checklist. */
	confirmReplace: (progressCount: number) => Promise<boolean>;
	/** Render this next to the form. */
	replacePrompt: React.ReactNode;
}

export function useChecklistReplaceGuard(formType: 'work_order' | 'inspection'): UseChecklistReplaceGuard {
	const [count, setCount] = useState<number | null>(null);
	/* A ref, not state: settling must not wait on a re-render. */
	const resolverRef = useRef<((replace: boolean) => void) | null>(null);

	const confirmReplace = useCallback((progressCount: number) => new Promise<boolean>((resolve) => {
		resolverRef.current = resolve;
		setCount(progressCount);
	}), []);

	const settle = useCallback((replace: boolean) => {
		setCount(null);
		const resolve = resolverRef.current;
		resolverRef.current = null;
		resolve?.(replace);
	}, []);

	const job = formType === 'inspection' ? 'inspection' : 'work order';
	const work = formType === 'inspection' ? 'ratings, notes or photos' : 'ticks, notes or photos';
	const items = count === 1 ? '1 item on the current checklist already has' : `${count} items on the current checklist already have`;

	const replacePrompt = (
		<ConfirmModal
			isOpen={count !== null}
			onClose={() => settle(false)}
			onConfirm={() => settle(true)}
			title="Replace this checklist?"
			message={`${items} work recorded — ${work}. Switching to a different checklist removes that work. Photos stay on the ${job} but are no longer linked to an item.`}
			confirmLabel="Replace checklist"
			cancelLabel="Keep current checklist"
			variant="danger"
			dataRef="organism-checklist-replace-prompt"
		/>
	);

	return { confirmReplace, replacePrompt };
}

export default useChecklistReplaceGuard;
